// Settings → Security and Profile → "Where you're signed in": the small words around the lines
// the server sends (DESIGN-security.md, step 1). Pure — `securityWords.checks.mjs` pins them.
//
// ⚠️ WHAT A LINE SAYS ("Elon Test signed Carl Test out") IS THE SERVER'S
// (`services/security_log.describe`), so the screen, Profile and the Excel download can never
// disagree. This file only does the day headings, the times and "active 2 minutes ago".
//
// ⚠️ NO REACT AND NO IMPORTS: the checks import this file under plain node.

const pad = (n) => String(n).padStart(2, '0');
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The server sends times already in the company's own clock, with its offset
// ("2026-10-24T18:57:00+01:00"). These read the wall-clock parts straight off the string,
// so a laptop set to another time zone still shows the company's time.
function parts(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || '');
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], h: m[4], mi: m[5], date: `${m[1]}-${m[2]}-${m[3]}` };
}

/** "18:57" */
export function clock(iso) {
  const p = parts(iso);
  return p ? `${p.h}:${p.mi}` : '';
}

/** "08:02–14:10" for a folded line, else the one time. */
export function timeSpan(firstIso, lastIso) {
  if (!firstIso || clock(firstIso) === clock(lastIso)) return clock(lastIso);
  return `${clock(firstIso)}–${clock(lastIso)}`;
}

/** "Mon 19/10/2026" for "2026-10-19". */
export function dayDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00Z`);
  const [y, m, dd] = dateStr.split('-');
  return `${DAYS[d.getUTCDay()]} ${dd}/${m}/${y}`;
}

/** "2026-10-19" moved by `n` days. */
export function addDays(dateStr, n) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * A day's heading, in two parts so the screen can make the date stand out (Rob, 25/10/2026:
 * "it does not tell you" which day — "YESTERDAY" alone never said the date).
 * { lead: 'Today' | 'Yesterday' | null, date: 'Sun 25/10/2026' }. `todayDate` is "YYYY-MM-DD".
 */
export function dayHeadingParts(dateStr, todayDate) {
  if (!dateStr) return { lead: null, date: '' };
  let lead = null;
  if (todayDate && dateStr === todayDate) lead = 'Today';
  else if (todayDate && dateStr === addDays(todayDate, -1)) lead = 'Yesterday';
  return { lead, date: dayDate(dateStr) };
}

/** "Today · Sun 25/10/2026", "Yesterday · Sat 24/10/2026", or "Mon 19/10/2026". */
export function dayHeading(dateStr, todayDate) {
  const { lead, date } = dayHeadingParts(dateStr, todayDate);
  return lead ? `${lead} · ${date}` : date;
}

/** "active now", "active 14 minutes ago", "active 2 hours ago", "active on 19/10/2026". */
export function activeAgo(iso, nowMs = Date.now()) {
  const at = Date.parse(iso || '');
  if (!Number.isFinite(at)) return '';
  const mins = Math.floor((nowMs - at) / 60000);
  if (mins < 2) return 'active now';
  if (mins < 60) return `active ${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return hours === 1 ? 'active 1 hour ago' : `active ${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? 'active yesterday' : `active ${days} days ago`;
  const p = parts(iso);
  return p ? `active on ${pad(p.d)}/${pad(p.mo)}/${p.y}` : '';
}

/** Today's date ("YYYY-MM-DD") on the company's clock, from any time the server sent. */
export function todayFrom(serverIso, nowMs = Date.now()) {
  const m = /([+-])(\d{2}):(\d{2})$/.exec(serverIso || '');
  const offsetMin = m ? (m[1] === '-' ? -1 : 1) * (+m[2] * 60 + +m[3]) : 0;
  return new Date(nowMs + offsetMin * 60000).toISOString().slice(0, 10);
}

/** Only a two-letter code becomes a picture path — nothing else reaches the URL. */
export function flagSrc(code) {
  const c = String(code || '').toLowerCase();
  return /^[a-z]{2}$/.test(c) ? `/flags/4x3/${c}.svg` : null;
}

export const ICON_STYLES = {
  ok: { char: '✓', cls: 'bg-green-500/15 text-green-700 dark:text-green-400' },
  bad: { char: '✕', cls: 'bg-red-500/15 text-red-600' },
  locked: { char: '🔒', cls: 'bg-red-500/15' },
  key: { char: '🔑', cls: 'bg-blue-500/15' },
  shield: { char: '🛡', cls: 'bg-green-500/15' },
  setting: { char: '⚙', cls: 'bg-amber-500/15' },
  support: { char: '🛟', cls: 'bg-purple-500/15' },
  out: { char: '↩', cls: 'bg-secondary text-muted-foreground' },
  // Step 2 (25/10/2026): what people did from the health card and the email.
  mail: { char: '✉', cls: 'bg-blue-500/15' },
  unlock: { char: '🔓', cls: 'bg-green-500/15' },
  alarm: { char: '⚠', cls: 'bg-red-500/15 text-red-600' },
  // API step 2 (25/10/2026): a webhook address added, or paused after 3 days of failures.
  hook: { char: '↗', cls: 'bg-blue-500/15' },
};
